import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DataTablePagination } from '@/components/DataTablePagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { SearchInput } from '@/components/ui/search-input';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ListChecks, RefreshCw } from 'lucide-react';

import type { MonitoramentoJob, TipoJob } from '../../../api/monitoramento';
import { useJobs } from '../../../hooks/useMonitoramentoQuery';
import { getUserVisibleErrorMessage } from '@/lib/errors';
import { fullDate } from '@/lib/datetime';

const PAGE_SIZE_PADRAO = 20;
const PAGE_SIZE_OPCOES = [10, 20, 50, 100];

/**
 * Aba "Jobs (autenticador)": visão micro para o operador investigar
 * "por que falhou esse processo/peticionamento específico". Combina
 * `job_carga_processo`, `job_carga_tramitacao`, `job_peticao_tramitacao`
 * e `job_peticao_status_portal` em uma única tabela paginada server-side.
 *
 * <p>Filtros server-side: tipo + status. Busca textual é client-side, sobre
 * a página atual — suficiente para o caso de uso de "achar o registro que
 * vejo agora" sem complicar o backend com LIKEs caros.
 */
export interface JobsTabelaProps {
  /**
   * Filtro de alvo aplicado no servidor — quando passado, oculta jobs de
   * outros alvos. Usado pelo deep-link `?alvo=` do dashboard. A chip de
   * "Filtrado por alvo: X" é renderizada pelo container, não aqui.
   */
  alvo?: string;
}

export function JobsTabela({ alvo }: JobsTabelaProps = {}) {
  const [tipo, setTipo] = useState<TipoJob | 'TODOS'>('TODOS');
  const [statusFiltro, setStatusFiltro] = useState<string>('TODOS');
  const [busca, setBusca] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_PADRAO);

  // Reseta para a primeira página quando o `alvo` (prop externa) muda — evita
  // "página 5 de 0" depois de trocar o deep-link `?alvo=`. Padrão "adjusting
  // state when a prop changes" da doc do React: comparação durante render +
  // setState condicional. Para `tipo` e `statusFiltro` (state local), o reset
  // é feito direto nos onChange dos selects abaixo.
  const [alvoAnterior, setAlvoAnterior] = useState(alvo);
  if (alvo !== alvoAnterior) {
    setAlvoAnterior(alvo);
    setPage(1);
  }

  const filtros = useMemo(
    () => ({
      tipo: tipo === 'TODOS' ? undefined : tipo,
      status: statusFiltro === 'TODOS' ? undefined : statusFiltro,
      alvo,
      page,
      pageSize,
    }),
    [tipo, statusFiltro, alvo, page, pageSize],
  );

  const { data, isLoading, isFetching, isError, error, refetch } = useJobs(filtros, 30_000);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const jobsExibidos = useMemo(() => {
    const jobs = data?.jobs ?? [];
    if (!busca.trim()) return jobs;
    const termo = busca.trim().toLowerCase();
    // Busca substring em chave de negócio, alvo e fragmento de erro — basta
    // para o caso de uso típico ("achar o registro que vejo agora").
    return jobs.filter((j) =>
      [j.chave, j.alvo, j.erro].some((v) => v?.toLowerCase().includes(termo)),
    );
  }, [data, busca]);

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] max-w-xs">
            <SearchInput
              value={busca}
              onChange={setBusca}
              placeholder="Buscar processo, alvo ou erro (página atual)…"
            />
          </div>
          <select
            value={tipo}
            onChange={(e) => {
              setTipo(e.target.value as TipoJob | 'TODOS');
              setPage(1);
            }}
            className="cursor-pointer rounded border bg-background px-2 py-1 text-sm"
          >
            <option value="TODOS">Todos os tipos</option>
            <option value="carga_processo">Carga de processo</option>
            <option value="carga_tramitacao">Download de documento</option>
            <option value="peticao_tramitacao">Peticionamento</option>
            <option value="peticao_status_portal">Status no portal</option>
          </select>
          <select
            value={statusFiltro}
            onChange={(e) => {
              setStatusFiltro(e.target.value);
              setPage(1);
            }}
            className="cursor-pointer rounded border bg-background px-2 py-1 text-sm"
          >
            <option value="TODOS">Todos os status</option>
            {/* Carga de processo */}
            <option value="ENFILEIRADO">ENFILEIRADO</option>
            <option value="EM_EXECUCAO">EM_EXECUCAO</option>
            <option value="METADADOS_CONCLUIDOS">METADADOS_CONCLUIDOS</option>
            <option value="FALHA">FALHA</option>
            {/* Carga de tramitação (download) */}
            <option value="PULADO_SEM_URL">PULADO_SEM_URL</option>
            {/* Peticionamento */}
            <option value="PENDENTE">PENDENTE</option>
            <option value="SUCESSO">SUCESSO</option>
            <option value="FALHA_TEMPORARIA">FALHA_TEMPORARIA</option>
            <option value="AGUARDANDO_CONFERENCIA_MANUAL">AGUARDANDO_CONFERENCIA_MANUAL</option>
            <option value="FALHA_FINAL">FALHA_FINAL</option>
            <option value="CANCELADO">CANCELADO</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Atualizar</span>
          </Button>
        </div>

        {isError && !data ? (
          <ErrorState
            title="Não foi possível carregar os jobs"
            description={
              getUserVisibleErrorMessage(error) ??
              'Falha desconhecida ao consultar o autenticador.'
            }
            actions={[{ label: 'Tentar novamente', onClick: () => refetch() }]}
          />
        ) : isLoading ? (
          <Spinner size={32} label="Carregando jobs" />
        ) : jobsExibidos.length === 0 ? (
          <EmptyState
            icon={<ListChecks className="size-8" />}
            title={
              busca
                ? 'Nenhum job na página atual corresponde à busca'
                : 'Nenhum job para os filtros atuais'
            }
            description={
              busca
                ? 'Limpe a busca ou navegue para outras páginas.'
                : 'Ajuste os filtros ou aguarde novos jobs serem enfileirados.'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="hidden sm:table-cell">Referência</TableHead>
                  <TableHead className="hidden md:table-cell">Usuário</TableHead>
                  <TableHead className="hidden lg:table-cell">reqId</TableHead>
                  <TableHead className="hidden md:table-cell">Alvo</TableHead>
                  <TableHead className="hidden sm:table-cell">Status</TableHead>
                  <TableHead className="hidden md:table-cell text-right">Tent.</TableHead>
                  <TableHead className="hidden md:table-cell">Iniciado</TableHead>
                  <TableHead className="hidden lg:table-cell">Finalizado</TableHead>
                  <TableHead className="hidden md:table-cell">Erro</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobsExibidos.map((j) => (
                  <LinhaJob key={`${j.tipo}-${j.id}`} job={j} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Paginação server-side: total vem do autenticador e cobre os
            filtros de tipo+status (não a busca local). Oculta quando o
            fetch falhou e não há cache para paginar. */}
        {!(isError && !data) && (
        <DataTablePagination
          page={page}
          pageSize={pageSize}
          total={total}
          pages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          pageSizeOptions={PAGE_SIZE_OPCOES}
        />
        )}
      </CardContent>
    </Card>
  );
}

function LinhaJob({ job }: { job: MonitoramentoJob }) {
  const reqIdCurto = job.request_id ? job.request_id.slice(0, 8) : null;
  // Navegação interna para a tela de logs filtrada por request_id. Usa Link
  // do react-router (não <a target=_blank>) — a tela de logs é parte do mesmo
  // SPA admin e abrir nova aba aqui só estourava popup que ficava órfão da
  // navegação de "voltar para Jobs". Filtro estruturado via ?reqId= (não
  // search=) para mandar ao backend como label selector no Loki.
  const observabilityTo = job.request_id
    ? `/app/admin/observabilidade?reqId=${encodeURIComponent(job.request_id)}&period=24h&level=ALL`
    : null;
  return (
    <TableRow>
      <TableCell className="text-sm">
        <div className="flex flex-col gap-0.5">
          <span>{rotuloTipo(job.tipo)}</span>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground sm:hidden">
            <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${classesStatus(job.status)}`}>
              {job.status}
            </span>
            {job.iniciado_em && <span>{formatarDt(job.iniciado_em)}</span>}
            {job.tentativas != null && job.tentativas > 1 && (
              <span>{job.tentativas} tent.</span>
            )}
            {job.erro && (
              <span
                className="block w-full break-words text-red-700 dark:text-red-400"
              >
                {job.erro}
              </span>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden sm:table-cell font-mono text-xs">{job.chave}</TableCell>
      <TableCell className="hidden md:table-cell font-mono text-xs">{formatarCpf(job.cpf)}</TableCell>
      <TableCell className="hidden lg:table-cell font-mono text-xs">
        {observabilityTo && reqIdCurto ? (
          <Link
            to={observabilityTo}
            title={job.request_id ?? ''}
            className="text-primary underline-offset-2 hover:underline"
          >
            {reqIdCurto}
          </Link>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell className="hidden md:table-cell font-mono text-xs">{job.alvo ?? '—'}</TableCell>
      <TableCell className="hidden sm:table-cell">
        <span className={`rounded px-2 py-0.5 text-xs font-medium ${classesStatus(job.status)}`}>
          {job.status}
        </span>
      </TableCell>
      <TableCell className="hidden md:table-cell text-right font-mono text-xs">{job.tentativas ?? '—'}</TableCell>
      <TableCell className="hidden md:table-cell text-xs">{formatarDt(job.iniciado_em)}</TableCell>
      <TableCell className="hidden lg:table-cell text-xs">{formatarDt(job.finalizado_em)}</TableCell>
      <TableCell className="hidden md:table-cell text-xs text-red-700 dark:text-red-400">
        <div className="max-h-24 w-[360px] overflow-y-auto [overflow-wrap:anywhere]">{job.erro ?? ''}</div>
      </TableCell>
    </TableRow>
  );
}

/**
 * Formata CPF cru (11 dígitos) como xxx.xxx.xxx-xx. Se vier null, vazio ou
 * em formato inesperado, devolve "—" — aba é diagnóstico, não vale quebrar
 * a tela por dado mal formado.
 */
function formatarCpf(cpf: string | null): string {
  if (!cpf) return '—';
  const apenasDigitos = cpf.replace(/\D/g, '');
  if (apenasDigitos.length !== 11) return '—';
  return `${apenasDigitos.slice(0, 3)}.${apenasDigitos.slice(3, 6)}.${apenasDigitos.slice(6, 9)}-${apenasDigitos.slice(9)}`;
}

function rotuloTipo(tipo: TipoJob): string {
  switch (tipo) {
    case 'carga_processo':
      return 'Carga';
    case 'carga_tramitacao':
      return 'Download doc';
    case 'peticao_tramitacao':
      return 'Peticionamento';
    case 'peticao_status_portal':
      return 'Status no portal';
  }
}

function formatarDt(iso: string | null): string {
  if (!iso) return '—';
  return fullDate(iso);
}

/**
 * Mapeia o status (vocabulário diferente entre tipos de job) para uma cor
 * coerente. Aceita strings desconhecidas (qualquer status novo aparece em
 * cinza neutro até decidirmos a cor — não quebra a tela).
 */
function classesStatus(status: string): string {
  const ok = new Set(['SUCESSO', 'METADADOS_CONCLUIDOS', 'TERMINAL']);
  const emAndamento = new Set(['ENFILEIRADO', 'EM_EXECUCAO', 'PENDENTE']);
  const aviso = new Set([
    'FALHA_TEMPORARIA',
    'AGUARDANDO_CONFERENCIA_MANUAL',
    'PULADO_SEM_URL',
    'ACOES_TERMINAIS_PENDENTES',
  ]);
  const erro = new Set(['FALHA', 'FALHA_FINAL', 'EXPIRADO']);
  const cancelado = new Set(['CANCELADO']);
  if (ok.has(status)) {
    return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
  }
  if (emAndamento.has(status)) {
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
  }
  if (aviso.has(status)) {
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
  }
  if (erro.has(status)) {
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  }
  if (cancelado.has(status)) {
    return 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
  }
  return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
}
